import { computed, Injectable, inject } from '@angular/core';
import { signalMethod } from '@ngrx/signals';
import { Router, NavigationEnd } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { UserDataService } from 'src/data-store/user-data.service';
import { ILoggedUser } from 'src/models/user.model';
import dayjs from 'dayjs';
import { LogoutService } from './logout.service';
import { ConfigService } from './json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { DeviceService } from './device.service';

@Injectable({ providedIn: 'root' })
export class IdleService {
  private idle = inject(Idle);
  private router = inject(Router);
  private userDataService = inject(UserDataService);
  private logoutService = inject(LogoutService);
  private configService = inject(ConfigService);
  private deviceService = inject(DeviceService);

  public constructor() {
    if(this.deviceService.isNativePlatform) {
      return;
    }
    // Configure idle detection
    const idleSecondsTimeout = this.configService.getConfig(EnvironmentKeys.logoutAfterIdleForMinutes) * 60;
    this.idle.setIdle(idleSecondsTimeout);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // When idle timeout occurs
    this.idle.onIdleStart.subscribe(() => this.userDataService.setIsIdle(true));

    // Reset on navigation
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.resetIdleCountdown();
      }
    });

    this.idleDetectionForLoggedUserEffect(this.userDataService.loggedUser);
    this.logoutWhenIdleEffect(this.logoutWhenIdleParams);
  }

  private logoutWhenIdleParams = computed(() => ({
    isIdle: this.userDataService.isIdle(),
    loggedUser: this.userDataService.loggedUser(),
  }));

  public readonly idleDetectionForLoggedUserEffect = signalMethod<ILoggedUser | null>((user) => {
    if (user) {
      this.resetIdleCountdown();
    } else {
      // User is logged out, stop idle detection
      this.idle.stop();
      this.userDataService.setIsIdle(false);
      this.userDataService.setLastActive(null);
    }
  });

  private resetIdleCountdown() {
    const user = this.userDataService.loggedUser();
    if (!user) return; // Don't reset if no user is logged in

    this.idle.watch();
    this.userDataService.setIsIdle(false);
    this.userDataService.setLastActive(dayjs().format());
  }

  public readonly logoutWhenIdleEffect = signalMethod<{
    isIdle: boolean;
    loggedUser: unknown;
  }>(({ isIdle, loggedUser }) => {
    if (isIdle && loggedUser) {
      this.logoutService.logOut({withMessage: 'idleLogoutMessage'});
    }
  });
}
